import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { Alert, Box, Stack, Tooltip, Typography } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { flatten } from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AI_GENERATION_PROGRESS,
  ARTIFACT_TYPES,
  MATERIAL_CANVAS_NOTE_EDITOR_ALERT_TYPE
} from "../../constants";
import { activityAtom } from "../../stores/jotai/activities/activities";
import {
  baseAtom,
  checkEntryInsertionType,
  setCanvasSliderValue,
  setShowCanvasNotes
} from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { hasCanvasWriteAccess } from "../../stores/jotai/permissions";
import { useGenerateBMC } from "../../tanstackQuery/queries/bmc/useGenerateBMC";
import { useDeleteCanvasBulkEntries } from "../../tanstackQuery/queries/canvas/useDeleteCanvasBulkEntries";
import yjsObjectToPlainString from "../../util/yjsObjectToPlainString";
import AlertDialog from "../app/alert-dialog";
import IwButton from "../app/IwButton";
import MaterialArtifactEditor from "../app/material-artifact-editor";
import MaterialModal from "../app/material-modal";
import {
  aiCanvasGenerationAtom,
  setAiCanvasGenerationProgress
} from "../../stores/jotai/generation-progress/ai/aiCanvasGeneration";
import { renderErrorToast } from "../../../lib/ErrorToastMessage";
import { INITIAL_CANVAS_DATA_ATOM_VALUE } from "../../stores/jotai/canvas/canvasData";

const showingCanvasNotesAtom = selectAtom(
  baseAtom,
  (base) => base.showingCanvasNotes
);
const roleSnapshotAtom = selectAtom(baseAtom, (base) => base.roleSnapshot);
export default function MaterialCanvasNotesEditor(props: any) {
  const { fromHome = false, canvasData = INITIAL_CANVAS_DATA_ATOM_VALUE } = props;
  const activities = useAtomValue(activityAtom);

  const navigate = useNavigate();

  //businessIdeaTextInput is replaced with base.canvas.description to solve the rerendering and it is only being used in this component
  let [businessIdeaTextInput, setBusinessIdeaTextInput] = useState({});

  const [isDeletingCanvasEntires, setIsDeletingCanvasEntires] = useState(false);
  const [base, setBase] = useAtom(baseAtom);
  const [aiCanvaseGenerationState, setAiCanvaseGenerationState] = useAtom(
    aiCanvasGenerationAtom
  );
  const discoverySession = useAtomValue(discoverySessionAtom);
  const dynamicArtifactID = base.team.groupID;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showAIConfirmationModal, setShowAIConfirmationModal] = useState(false);
  const [canvasStatusModals, setCanvasStatusModals] = useState({
    inputError: false,
    generalError: false,
  })

  const [isAlertGenerateActive, setIsAlertGenerateActive] = useState(false);
  const [isAlertDeleteActive, setIsAlertDeleteActive] = useState(false);
  const [roleSnapshot] = useAtom(roleSnapshotAtom);
  const [showingCanvasNotes] = useAtom(showingCanvasNotesAtom);
  const { keycloak } = useKeycloak();

  const client = useQueryClient();
  const { mutateAsync: handleDeleteCanvasEntries } = useDeleteCanvasBulkEntries(
    { accessToken: keycloak?.token },
    {},
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["canvas"] });
      }
    }
  );

  const { mutateAsync: handleDeleteAICanvasEntries } =
    useDeleteCanvasBulkEntries(
      { accessToken: keycloak?.token },
      { entryInsertionType: "AI" },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["canvas"] });
        }
      }
    );

  const { mutateAsync: generateBMC } = useGenerateBMC(
    { accessToken: keycloak?.token },
    {
      onSuccess: (res) => {
        client.invalidateQueries({ queryKey: ["canvas"] });
        client.invalidateQueries({ queryKey: ["canvas_layout"] });

        // Hide the modal
        setShowCanvasNotes(setBase, false);
        const groupID = base.team.groupID;
        // @ts-ignore
        if (!!res?.generationStarted) {
          setAiCanvasGenerationProgress(
            setAiCanvaseGenerationState,
            groupID,
            AI_GENERATION_PROGRESS.IN_PROGRESS
          );
        } else {
          setAiCanvasGenerationProgress(
            setAiCanvaseGenerationState,
            groupID,
            AI_GENERATION_PROGRESS.NOT_STARTED
          );
        }
        if (fromHome) {
          pageChange("/canvas");
        }
      }
    }
  );

  const pageChange = (target) => {
    navigate(target);
  };

  //! This is the function that will be called when the user clicks on the "Generate Canvas" or "Delete All Hypotheses" buttons
  const activate = (type) => {
    if (type === MATERIAL_CANVAS_NOTE_EDITOR_ALERT_TYPE.DELETE) {
      setIsAlertDeleteActive(true);
      setTimeout(() => {
        setIsAlertDeleteActive(false);
      }, 10000);
    } else if (type === MATERIAL_CANVAS_NOTE_EDITOR_ALERT_TYPE.GENERATE) {
      setIsAlertGenerateActive(true);
      setTimeout(() => {
        setIsAlertGenerateActive(false);
      }, 10000);
    }
  };
  const confirmDeleteCanvas = (e, isAi: boolean = false) => {
    e.stopPropagation();
    e.preventDefault();
    setShowCanvasNotes(setBase, false);
    if (isAi) setShowAIConfirmationModal(true);
    else setShowConfirmationModal(true);
  };

  const handleDeleteCanvas = (isDeleteAI: boolean = false) => {
    setIsDeletingCanvasEntires(true);

    const handleDelete = isDeleteAI
      ? handleDeleteAICanvasEntries
      : handleDeleteCanvasEntries;

    handleDelete({
      canvasID: canvasData?.canvasID,
      teamID: base?.team?.groupID
    })
      .then(() => {
        // Hide the modal
        setShowCanvasNotes(setBase, false);
        setCanvasSliderValue(setBase, activities?.length);
      })
      .catch((error) => {
        renderErrorToast(error);
      })
      .finally(() => {
        setIsDeletingCanvasEntires(false);
        setShowConfirmationModal(false);
        setShowAIConfirmationModal(false);
      });
  };

  const handleGenerateCanvas = async (retry = false) => {
    // setIsGeneratingCanvas(true);

    const groupID = base?.team?.groupID;

    // Generate the canvas
    if (!businessIdeaTextInput) {
      if (retry) {
        // renderErrorToast("Please re-enter your idea.");
        setCanvasStatusModals(prev => ({...prev, inputError: true}))
        setShowConfirmationModal(false);
        return;
      } else {
        setTimeout(() => handleGenerateCanvas(true), 0);
        return;
      }
    }
    if (!canvasData) {
      setCanvasStatusModals(prev => ({...prev, generalError: true}))
      // renderErrorToast("Canvas details isn't available! Try Again Later.");
      setShowConfirmationModal(false);
      return;
    }
    await generateBMC({
      groupID,
      description: businessIdeaTextInput,
      canvasType: canvasData?.type,
      canvasID: canvasData?.canvasID,
      sessionID: discoverySession?.sessionID
    });
  };

  const isCanvasEmpty = () => {
    const categories = flatten(
      canvasData?.categories?.map((category) => category?.entries)
    );
    const categoryInUse = categories?.find(
      (entry: any) => entry.state !== "Deleted"
    );
    return !categoryInUse;
  };

  const onClickCanvasDelete = (e, isAi: boolean = false) => {
    if (!isCanvasEmpty()) {
      confirmDeleteCanvas(e, isAi);
    } else {
      activate("delete");
    }
  };

  const canvasIntertionTypeCount = checkEntryInsertionType(canvasData);
  return (
    <>
      <AlertDialog
        dialogWidth="400px"
        open={canvasStatusModals.inputError}
        onClose={() => setCanvasStatusModals(prev => ({...prev, inputError: false}))}
        title="Please re-enter your idea."
        confirmButton={
          <IwButton
            variant="outlined"
            onClick={() => {
              setCanvasStatusModals(prev => ({...prev, inputError: false}))
            }}
          >
            Dismiss
          </IwButton>
        }
      />
      <AlertDialog
        dialogWidth="400px"
        open={canvasStatusModals.generalError}
        onClose={() => setCanvasStatusModals(prev => ({...prev, generalError: false}))}
        title="An error occurred. Please try again later."
        confirmButton={
          <IwButton
            variant="outlined"
            onClick={() => {
              setCanvasStatusModals(prev => ({...prev, generalError: false}));
            }}
          >
            Dismiss
          </IwButton>
        }
      />

      <AlertDialog
        title="Delete All Hypotheses?"
        description="Confirming this action will delete all of the hypotheses, comments, links and histories in your canvas. This action is not reversible. Are you sure you want to continue?"
        open={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false);
        }}
        confirmButton={
          <IwButton
            variant="contained"
            onClick={() => handleDeleteCanvas()}
            loading={isDeletingCanvasEntires}
            disabled={isDeletingCanvasEntires}
          >
            Confirm Deletion
          </IwButton>
        }
        abortButton={
          <IwButton
            variant="outlined"
            onClick={() => {
              setShowConfirmationModal(false);
            }}
          >
            Cancel
          </IwButton>
        }
      />

      <AlertDialog
        title="Delete All AI Hypotheses?"
        description="Confirming this action will delete all of the ai hypotheses, comments, links and histories in your canvas. This action is not reversible. Are you sure you want to continue ?"
        open={showAIConfirmationModal}
        onClose={() => {
          setShowAIConfirmationModal(false);
        }}
        confirmButton={
          <IwButton
            variant="contained"
            onClick={() => handleDeleteCanvas(true)}
            loading={isDeletingCanvasEntires}
            disabled={isDeletingCanvasEntires}
          >
            Confirm Deletion
          </IwButton>
        }
        abortButton={
          <IwButton
            variant="outlined"
            onClick={() => {
              setShowAIConfirmationModal(false);
            }}
          >
            Cancel
          </IwButton>
        }
      />

      <MaterialModal
        title="Generate Business model with AI"
        styleOverrides={{
          container: {
            "& .MuiDialog-paper": { maxWidth: "650px", minWidth: "500px" }
          }
        }}
        open={showingCanvasNotes === true}
        handleDialogClose={() => {
          setShowCanvasNotes(setBase, false);
        }}
      >
        <Stack spacing={1} >
          <Stack spacing={1}>
            <Typography variant="subtitle1" textAlign="left">
              Tell us about your business idea.
              <br></br>
              <br></br>
              With this information, we&apos;ll craft a custom canvas for you to
              visualize and refine your concept. You can always fine-tune the
              details to perfectly align with your vision later.
            </Typography>
          </Stack>
          <Box sx={{ height: "calc(450px - 157px)"}}>
            <MaterialArtifactEditor
              isReadOnly={!hasCanvasWriteAccess(roleSnapshot)}
              artifactID={dynamicArtifactID}
              artifactType={ARTIFACT_TYPES.CANVAS_NOTE}
              onChange={(e) =>
                setBusinessIdeaTextInput(yjsObjectToPlainString(e))
              }
              useToolBar={false}
            />
          </Box>
         
          
          {/* Insert a buttom on the bottom right corner of the modal that says "Generate BMC", and a second, red button that says delete canvas */}
         {isAlertDeleteActive || isAlertGenerateActive ? (
            <Stack  sx={{
              paddingTop: "40px"
            }}>
               <Alert severity="warning" variant="outlined">
              {isAlertGenerateActive
                ? "You must delete your canvas before generating a new one."
                : isAlertDeleteActive
                ? "Your canvas is already empty"
                : null}
            </Alert>
          </Stack>
          ) : null}
         
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "flex-end",
              paddingTop: (isAlertDeleteActive || isAlertGenerateActive) ? "10px" : "48px"
            }}
            spacing={1}
          >
            <IwButton
              variant="outlined"
              loading={isDeletingCanvasEntires}
              disabled={
                isDeletingCanvasEntires ||
                !roleSnapshot?.isTeamMember ||
                canvasIntertionTypeCount.AI === 0
              }
              onClick={(event) => onClickCanvasDelete(event, true)}
            >
              Delete All AI Hypotheses
            </IwButton>
            <IwButton
              variant="outlined"
              loading={isDeletingCanvasEntires}
              disabled={
                isDeletingCanvasEntires ||
                !roleSnapshot?.isTeamMember ||
                !canvasIntertionTypeCount.hasData
              }
              onClick={(event) => onClickCanvasDelete(event)}
            >
              Delete All Hypotheses
            </IwButton>
            {(base.team?.isAiCanvasGenerationEnabled) ?
            (
            <Box sx={{ marginLeft: "auto" ,display: 'inline-block'}}>
            <IwButton
              variant="contained"
              startIcon={<AutoFixHighIcon />}
              
              onClick={() => {
                if (!isCanvasEmpty() && canvasIntertionTypeCount.AI > 0)
                  activate("generate");
                else handleGenerateCanvas();
              }}
            >
              Generate Canvas
            </IwButton>
            </Box>) : null}
         
          </Stack>
        </Stack>
      </MaterialModal>
    </>
  );
}
